import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AppLayout } from "../shared/layout";
import { uploadPaymentWithExcel } from "@/data/requests/payment-requests";
import { getUsers } from "@/data/requests/user-requests";
import { filterRelatedUsers } from "@/utils/related-user-options";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Receipt,
  FileText,
  MapPin,
  Tag,
  Calendar,
  Mail,
  Upload,
  User,
} from "lucide-react";

export function PaymentAddScreen() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const pageSettings = {
    title: "Create Payment",
    actions: [
      {
        name: "Go To List",
        color: "blue",
        icon: "add-icon",
        onClick: () => {
          navigate("/payments");
        },
      },
    ],
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  // Kullanıcı listesini çek
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
  });

  const mutation = useMutation({
    mutationKey: ["createPayment"],
    mutationFn: (data) => {
      return uploadPaymentWithExcel(data);
    },
    onSuccess: (response) => {
      toast({
        title: "Success",
        description: "Payment created successfully",
      });
      navigate("/payments");
    },
    onError: (error) => {
      console.log("error", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response.data.message,
      });
    },
  });

  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("file", data.excelFile[0]);
    formData.append("name", data.name?.trim() || "");
    formData.append("country", data.country?.trim() || "");
    formData.append("invoiceId", data.invoiceId?.trim() || "");
    // mailBodyMessage HTML içerebilir, sadece baş ve sondaki boşlukları temizle
    formData.append("mailBodyMessage", data.mailBodyMessage?.trim() || "");
    formData.append("type", data.type?.trim() || "DEFAULT");
    formData.append("minAcceptedInvoiceDate", data.minAcceptedInvoiceDate?.trim() || "");
    formData.append("relatedUser", data.relatedUser?.trim() || "");
    
    mutation.mutate(formData);
  };

  if (mutation.isPending) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center p-12">
          <div className="text-center">
            <Upload className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
            <p className="text-lg font-medium">Creating payment...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={pageSettings.title} actions={pageSettings.actions}>
      <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Receipt className="h-5 w-5 text-green-600" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Payment Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter payment name"
                    className="mt-1.5"
                    {...register("name", {
                      required: "Name is required",
                    })}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="invoiceId" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Invoice ID *
                  </Label>
                  <Input
                    id="invoiceId"
                    type="text"
                    placeholder="Year-Month (e.g., 2024-January)"
                    className="mt-1.5"
                    {...register("invoiceId", {
                      required: "Invoice ID is required",
                      pattern: {
                        value: /^[A-Za-z0-9\-\s]+$/,
                        message: "Invoice ID can only contain English letters, numbers, hyphens and spaces (no Turkish characters)",
                      },
                    })}
                  />
                  {errors.invoiceId && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.invoiceId.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Only English letters (A-Z, a-z), numbers and hyphens allowed
                  </p>
                </div>

                <div>
                  <Label htmlFor="country" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Country *
                  </Label>
                  <Input
                    id="country"
                    type="text"
                    placeholder="Enter country"
                    className="mt-1.5"
                    {...register("country", {
                      required: "Country is required",
                    })}
                  />
                  {errors.country && (
                    <p className="text-sm text-destructive mt-1">
                      {errors.country.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="type" className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    Payment Type
                  </Label>
                  <Select
                    onValueChange={(value) => setValue("type", value)}
                    defaultValue="DEFAULT"
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select payment type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Payment Types</SelectLabel>
                        <SelectItem value="DEFAULT">DEFAULT</SelectItem>
                        <SelectItem value="FIXED">FIXED</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="relatedUser" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Related User
                  </Label>
                  <Select
                    onValueChange={(value) => setValue("relatedUser", value)}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder={usersLoading ? "Loading users..." : "Select a user"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>System Users</SelectLabel>
                        {filterRelatedUsers(usersData?.data).map((user) => (
                          <SelectItem key={user.id} value={user.name || user.email}>
                            {user.name} {user.email ? `(${user.email})` : ""}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional: User associated with this payment
                  </p>
                </div>
              </div>

              <div>
                <Label
                  htmlFor="minAcceptedInvoiceDate"
                  className="flex items-center gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  Min Accepted Invoice Date
                </Label>
                <Input
                  id="minAcceptedInvoiceDate"
                  type="text"
                  placeholder="dd.mm.yyyy (e.g., 15.06.2024)"
                  className="mt-1.5"
                  {...register("minAcceptedInvoiceDate", {
                    pattern: {
                      value: /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/,
                      message: "Date format must be dd.mm.yyyy (e.g., 15.06.2024)",
                    },
                  })}
                />
                {errors.minAcceptedInvoiceDate && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.minAcceptedInvoiceDate.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Optional: Minimum date for invoice acceptance
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Mail Configuration */}
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="h-5 w-5 text-blue-600" />
                Mail Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div>
                <Label htmlFor="mailBodyMessage" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Mail Body Message
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  This message will be sent to all clients (not dynamic)
                </p>
                <ReactQuill
                  theme="snow"
                  onChange={(value) => setValue("mailBodyMessage", value)}
                  className="mt-1.5"
                />
                {errors.mailBodyMessage && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.mailBodyMessage.message}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* File Upload */}
          <Card>
            <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Upload className="h-5 w-5 text-purple-600" />
                Excel File Upload
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div>
                <Label htmlFor="excelFile" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Excel File *
                </Label>
                <Input
                  id="excelFile"
                  name="excelFile"
                  type="file"
                  accept=".xlsx,.xls"
                  className="mt-1.5"
                  {...register("excelFile", {
                    required: "Excel file is required",
                  })}
                />
                {errors.excelFile && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.excelFile.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Upload an Excel file (.xlsx or .xls) containing payment data
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/payments")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Upload className="mr-2 h-4 w-4" />
              Create Payment
            </Button>
          </div>
        </div>
      </form>
    </AppLayout>
  );
}
